import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  RefreshCcw, LogOut, Search, CheckCheck, Ban, Send, Eye, Activity, AlertTriangle, CheckCircle2, Plus, Trash2, Phone, Upload, FileDown, Loader2,
} from "lucide-react";
import { adminApi, getAdminToken, clearAdminToken, getBackendBase } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import { ReportsTab } from "../components/ReportsTab";
import { TelegramTab } from "../components/TelegramTab";

const fmtMoney = (v) => `${new Intl.NumberFormat("ru-RU").format(v || 0)} ₽`;
const fmtDate = (v) => (v ? new Date(v).toLocaleString("ru-RU") : "—");
const fmtPhone = (p) =>
  p && p.length === 11 ? `+${p[0]} ${p.slice(1, 4)} ${p.slice(4, 7)}-${p.slice(7, 9)}-${p.slice(9, 11)}` : `+${p}`;

const STATUS_META = {
  paid: { label: "Оплачен", cls: "bg-[hsl(var(--success))] text-white hover:bg-[hsl(var(--success))]" },
  pending: { label: "Ожидает", cls: "bg-[hsl(var(--warning))] text-[hsl(var(--warning-foreground))] hover:bg-[hsl(var(--warning))]" },
  failed: { label: "Ошибка", cls: "bg-[hsl(var(--destructive))] text-white hover:bg-[hsl(var(--destructive))]" },
  expired: { label: "Истёк", cls: "bg-muted text-muted-foreground hover:bg-muted" },
  cancelled: { label: "Отменён", cls: "bg-muted text-muted-foreground hover:bg-muted" },
};

const NOTIF_META = {
  mocked: { label: "MOCK ✓", cls: "border-[hsl(var(--warning))] text-[hsl(var(--warning-foreground))]" },
  sent: { label: "Отправлено", cls: "border-[hsl(var(--success))] text-[hsl(var(--success))]" },
  failed: { label: "Ошибка", cls: "border-[hsl(var(--destructive))] text-[hsl(var(--destructive))]" },
  pending: { label: "Ожидает", cls: "border-border text-muted-foreground" },
};

const STATUS_FILTERS = [
  { value: "", label: "Все" },
  { value: "pending", label: "Ожидают" },
  { value: "paid", label: "Оплачены" },
  { value: "failed", label: "Ошибки" },
  { value: "expired", label: "Истекли" },
  { value: "cancelled", label: "Отменены" },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [payments, setPayments] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [mockMode, setMockMode] = useState(false);
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthOpen, setHealthOpen] = useState(false);
  const [phones, setPhones] = useState([]);
  const [phonesTotal, setPhonesTotal] = useState(0);
  const [phoneSearch, setPhoneSearch] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newNote, setNewNote] = useState("");
  const [addingPhone, setAddingPhone] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const fileInputRef = useRef(null);
  const todayStr = new Date().toISOString().slice(0, 10);
  const monthStartStr = todayStr.slice(0, 8) + "01";
  const [exportFrom, setExportFrom] = useState(monthStartStr);
  const [exportTo, setExportTo] = useState(todayStr);
  const [exporting, setExporting] = useState(false);

  const logout = () => {
    adminApi.post("/admin/logout").catch(() => {});
    clearAdminToken();
    navigate("/admin/login");
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p, n, ph] = await Promise.all([
        adminApi.get("/admin/stats"),
        adminApi.get("/admin/payments", { params: { status: statusFilter || undefined, search: search || undefined, limit: 100 } }),
        adminApi.get("/admin/notifications", { params: { limit: 100 } }),
        adminApi.get("/admin/phones", { params: { search: phoneSearch || undefined } }),
      ]);
      setStats(s.data);
      setMockMode(s.data.mock_mode);
      setPayments(p.data.items);
      setNotifications(n.data.items);
      setPhones(ph.data.items);
      setPhonesTotal(ph.data.total);
    } catch (err) {
      if (err?.response?.status === 401) {
        logout();
        return;
      }
      toast.error("Не удалось загрузить данные");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, search, phoneSearch]);

  useEffect(() => {
    if (!getAdminToken()) {
      navigate("/admin/login");
      return;
    }
    loadAll();
  }, [loadAll, navigate]);

  // light auto-refresh
  useEffect(() => {
    const iv = setInterval(loadAll, 15000);
    return () => clearInterval(iv);
  }, [loadAll]);

  const simulate = async (id, kind) => {
    try {
      await adminApi.post(`/payments/${id}/simulate-${kind}`);
      toast.success(kind === "paid" ? "Платёж отмечен как оплаченный" : "Платёж отмечен как неуспешный");
      loadAll();
    } catch (err) {
      toast.error("Не удалось выполнить действие");
    }
  };

  const checkHealth = async () => {
    setHealthLoading(true);
    setHealthOpen(true);
    setHealth(null);
    try {
      const res = await adminApi.get("/admin/provider-health");
      setHealth(res.data);
    } catch (err) {
      setHealth({ healthy: false, stage: "request_failed", detail: "Не удалось выполнить проверку" });
    } finally {
      setHealthLoading(false);
    }
  };

  const addPhone = async (e) => {
    e.preventDefault();
    if (!newPhone.trim() || addingPhone) return;
    setAddingPhone(true);
    try {
      await adminApi.post("/admin/phones", { phone: newPhone.trim(), note: newNote.trim() || undefined });
      toast.success("Номер добавлен");
      setNewPhone("");
      setNewNote("");
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Не удалось добавить номер");
    } finally {
      setAddingPhone(false);
    }
  };

  const deletePhone = async (id) => {
    try {
      await adminApi.delete(`/admin/phones/${id}`);
      toast.success("Номер удалён");
      loadAll();
    } catch (err) {
      toast.error("Не удалось удалить номер");
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await adminApi.get("/admin/phones/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "velowallet-phones-template.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (err) {
      toast.error("Не удалось скачать шаблон");
    }
  };

  const onUploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = ""; // allow re-selecting same file
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      toast.error("Нужен файл Excel в формате .xlsx");
      return;
    }
    setUploading(true);
    setUploadResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await adminApi.post("/admin/phones/bulk-upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadResult(res.data);
      setUploadOpen(true);
      toast.success(`Добавлено номеров: ${res.data.added}`);
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Не удалось загрузить файл");
    } finally {
      setUploading(false);
    }
  };

  const exportPayments = async () => {
    if (exporting) return;
    if (exportFrom && exportTo && exportFrom > exportTo) {
      toast.error("Дата начала позже даты конца");
      return;
    }
    setExporting(true);
    try {
      const res = await adminApi.get("/admin/payments/export", {
        params: {
          date_from: exportFrom || undefined,
          date_to: exportTo || undefined,
          status: statusFilter || undefined,
        },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `velowallet-payments_${exportFrom || "all"}_${exportTo || "all"}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success("Файл Excel сформирован");
    } catch (err) {
      toast.error("Не удалось выгрузить платежи");
    } finally {
      setExporting(false);
    }
  };

  const retryNotif = async (id) => {
    try {
      await adminApi.post(`/admin/notifications/${id}/retry`);
      toast.success("Повторная отправка выполнена");
      loadAll();
    } catch (err) {
      toast.error("Не удалось повторить отправку");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-white border-b border-border sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-[hsl(var(--primary))] flex items-center justify-center">
              <span className="text-white font-display font-bold text-sm">N</span>
            </div>
            <div>
              <h1 className="font-display text-lg font-bold leading-tight">NORD — Платежи СБП</h1>
              <p className="text-xs text-muted-foreground">Панель администратора</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {mockMode && (
              <Badge data-testid="mock-mode-badge" className="bg-[hsl(var(--warning))] text-[hsl(var(--warning-foreground))] hover:bg-[hsl(var(--warning))] font-bold">
                MOCK MODE
              </Badge>
            )}
            <Button
              data-testid="provider-health-button"
              variant="outline"
              size="sm"
              onClick={checkHealth}
              disabled={healthLoading}
              className="gap-1.5"
            >
              <Activity size={15} className={healthLoading ? "animate-pulse" : ""} />
              Шлюз СБП
            </Button>
            <Button data-testid="admin-refresh-button" variant="outline" size="icon" onClick={loadAll} disabled={loading}>
              <RefreshCcw size={16} className={loading ? "animate-spin" : ""} />
            </Button>
            <Button data-testid="admin-logout-button" variant="outline" size="icon" onClick={logout}>
              <LogOut size={16} />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 flex flex-col gap-6">
        {/* stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card data-testid="stats-paid-today">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground font-medium">Оплачено сегодня</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="font-display text-3xl font-bold text-[hsl(var(--success))]">{fmtMoney(stats?.paid_today?.amount)}</div>
              <p className="text-xs text-muted-foreground mt-1">{stats?.paid_today?.count ?? 0} платежей</p>
            </CardContent>
          </Card>
          <Card data-testid="stats-paid-total">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground font-medium">Оплачено всего</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="font-display text-3xl font-bold">{fmtMoney(stats?.paid_total?.amount)}</div>
              <p className="text-xs text-muted-foreground mt-1">{stats?.paid_total?.count ?? 0} платежей</p>
            </CardContent>
          </Card>
          <Card data-testid="stats-pending">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground font-medium">Ожидают оплаты</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="font-display text-3xl font-bold text-[hsl(var(--warning))]">{stats?.pending?.count ?? 0}</div>
              <p className="text-xs text-muted-foreground mt-1">на сумму {fmtMoney(stats?.pending?.amount)}</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="payments">
          <TabsList>
            <TabsTrigger value="payments" data-testid="tab-payments">Платежи</TabsTrigger>
            <TabsTrigger value="reports" data-testid="tab-reports">Отчёты</TabsTrigger>
            <TabsTrigger value="phones" data-testid="tab-phones">Номера ({phonesTotal})</TabsTrigger>
            <TabsTrigger value="notifications" data-testid="tab-notifications">Уведомления брокера</TabsTrigger>
            <TabsTrigger value="telegram" data-testid="tab-telegram">Telegram-бот</TabsTrigger>
          </TabsList>

          {/* ===== payments tab ===== */}
          <TabsContent value="payments" className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  data-testid="admin-search-input"
                  placeholder="Поиск по телефону"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8 w-56"
                />
              </div>
              <div className="flex flex-wrap gap-1">
                {STATUS_FILTERS.map((f) => (
                  <Button
                    key={f.value}
                    data-testid={`filter-${f.value || "all"}`}
                    variant={statusFilter === f.value ? "default" : "outline"}
                    size="sm"
                    className={statusFilter === f.value ? "bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90" : ""}
                    onClick={() => setStatusFilter(f.value)}
                  >
                    {f.label}
                  </Button>
                ))}
              </div>
              <div className="flex items-center gap-2 ml-auto" data-testid="export-payments-block">
                <div className="flex gap-1">
                  <Button
                    data-testid="export-preset-today"
                    variant="outline"
                    size="sm"
                    className="h-9"
                    onClick={() => { const t = new Date().toISOString().slice(0, 10); setExportFrom(t); setExportTo(t); }}
                  >
                    Сегодня
                  </Button>
                  <Button
                    data-testid="export-preset-week"
                    variant="outline"
                    size="sm"
                    className="h-9"
                    onClick={() => { const t = new Date().toISOString().slice(0, 10); setExportFrom(new Date(Date.now() - 6 * 864e5).toISOString().slice(0, 10)); setExportTo(t); }}
                  >
                    Неделя
                  </Button>
                  <Button
                    data-testid="export-preset-month"
                    variant="outline"
                    size="sm"
                    className="h-9"
                    onClick={() => { const t = new Date().toISOString().slice(0, 10); setExportFrom(t.slice(0, 8) + "01"); setExportTo(t); }}
                  >
                    Месяц
                  </Button>
                </div>
                <Input
                  data-testid="export-date-from"
                  type="date"
                  value={exportFrom}
                  onChange={(e) => setExportFrom(e.target.value)}
                  className="w-36 h-9"
                  aria-label="Дата начала выгрузки"
                />
                <span className="text-muted-foreground text-sm">—</span>
                <Input
                  data-testid="export-date-to"
                  type="date"
                  value={exportTo}
                  onChange={(e) => setExportTo(e.target.value)}
                  className="w-36 h-9"
                  aria-label="Дата конца выгрузки"
                />
                <Button
                  data-testid="export-payments-button"
                  variant="outline"
                  size="sm"
                  onClick={exportPayments}
                  disabled={exporting}
                  className="gap-1.5 h-9"
                >
                  {exporting ? <Loader2 size={15} className="animate-spin" /> : <FileDown size={15} />}
                  Экспорт в Excel
                </Button>
              </div>
            </div>

            <Card>
              <CardContent className="p-0">
                <Table data-testid="admin-payments-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Время</TableHead>
                      <TableHead>Телефон</TableHead>
                      <TableHead className="text-right">Сумма</TableHead>
                      <TableHead>Статус</TableHead>
                      <TableHead>Брокер</TableHead>
                      <TableHead className="text-right">Действия</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                          Платежей пока нет
                        </TableCell>
                      </TableRow>
                    )}
                    {payments.map((p) => {
                      const sm = STATUS_META[p.status] || STATUS_META.pending;
                      return (
                        <TableRow key={p.id} data-testid="admin-payment-row">
                          <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(p.created_at)}</TableCell>
                          <TableCell className="tabular-nums font-medium whitespace-nowrap">{fmtPhone(p.phone)}</TableCell>
                          <TableCell className="text-right tabular-nums font-semibold whitespace-nowrap">{fmtMoney(p.amount)}</TableCell>
                          <TableCell><Badge className={sm.cls}>{sm.label}</Badge></TableCell>
                          <TableCell>
                            {p.status === "paid" ? (
                              <Badge variant="outline" className={p.broker_notified ? NOTIF_META.mocked.cls : NOTIF_META.pending.cls}>
                                {p.broker_notified ? "Уведомлён" : "Нет"}
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground text-xs">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {p.status === "pending" && mockMode && (
                              <div className="flex justify-end gap-1">
                                <Button
                                  data-testid="admin-simulate-paid-button"
                                  size="sm"
                                  className="bg-[hsl(var(--success))] hover:bg-[hsl(var(--success))]/90 text-white h-8"
                                  onClick={() => simulate(p.id, "paid")}
                                >
                                  <CheckCheck size={14} className="mr-1" /> Оплачен
                                </Button>
                                <Button
                                  data-testid="admin-simulate-failed-button"
                                  size="sm"
                                  variant="outline"
                                  className="h-8 text-[hsl(var(--destructive))]"
                                  onClick={() => simulate(p.id, "failed")}
                                >
                                  <Ban size={14} />
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ===== reports tab (§12) ===== */}
          <TabsContent value="reports">
            <ReportsTab />
          </TabsContent>

          {/* ===== allowed phones tab ===== */}
          <TabsContent value="phones" className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx"
                data-testid="bulk-upload-input"
                onChange={onUploadFile}
                className="hidden"
              />
              <Button
                data-testid="bulk-upload-button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 gap-1.5"
              >
                {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                Загрузить Excel
              </Button>
              <Button
                data-testid="download-template-button"
                variant="outline"
                onClick={downloadTemplate}
                className="gap-1.5"
              >
                <FileDown size={16} /> Скачать шаблон
              </Button>
              <span className="text-xs text-muted-foreground">
                Столбец A — телефон, B — комментарий. Форматы: +7 / 8 / 10 цифр.
              </span>
            </div>

            <form onSubmit={addPhone} className="flex flex-wrap items-center gap-2" data-testid="add-phone-form">
              <Input
                data-testid="new-phone-input"
                placeholder="+7 / 8 / 7XXXXXXXXXX"
                value={newPhone}
                onChange={(e) => setNewPhone(e.target.value)}
                className="w-56"
              />
              <Input
                data-testid="new-phone-note-input"
                placeholder="Комментарий (необязательно)"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                className="w-64"
              />
              <Button
                data-testid="add-phone-button"
                type="submit"
                disabled={addingPhone || !newPhone.trim()}
                className="bg-[hsl(var(--success))] hover:bg-[hsl(var(--success))]/90 text-white gap-1.5"
              >
                <Plus size={16} /> Добавить
              </Button>
              <div className="relative ml-auto">
                <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  data-testid="phone-search-input"
                  placeholder="Поиск номера"
                  value={phoneSearch}
                  onChange={(e) => setPhoneSearch(e.target.value)}
                  className="pl-8 w-48"
                />
              </div>
            </form>

            <Card>
              <CardContent className="p-0">
                <Table data-testid="admin-phones-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Телефон</TableHead>
                      <TableHead>Комментарий</TableHead>
                      <TableHead>Добавлен</TableHead>
                      <TableHead className="text-right">Действия</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {phones.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                          Номеров пока нет — добавьте первый
                        </TableCell>
                      </TableRow>
                    )}
                    {phones.map((p) => (
                      <TableRow key={p.id} data-testid="admin-phone-row">
                        <TableCell className="tabular-nums font-semibold whitespace-nowrap">
                          <span className="inline-flex items-center gap-2">
                            <Phone size={14} className="text-[hsl(var(--primary))]" />
                            {fmtPhone(p.phone)}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{p.note || "—"}</TableCell>
                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(p.created_at)}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            data-testid="delete-phone-button"
                            size="sm"
                            variant="outline"
                            className="h-8 text-[hsl(var(--destructive))]"
                            onClick={() => deletePhone(p.id)}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
            <p className="text-xs text-muted-foreground">
              Оплата возможна только с номеров из этого списка. Клиент может вводить номер с 8 — система приведёт его к +7 автоматически.
            </p>
          </TabsContent>

          {/* ===== notifications tab ===== */}
          <TabsContent value="notifications">
            <Card>
              <CardContent className="p-0">
                <Table data-testid="admin-notifications-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Время</TableHead>
                      <TableHead>Телефон</TableHead>
                      <TableHead className="text-right">Сумма</TableHead>
                      <TableHead>Статус</TableHead>
                      <TableHead>Попытки</TableHead>
                      <TableHead className="text-right">Действия</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {notifications.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                          Уведомлений пока нет
                        </TableCell>
                      </TableRow>
                    )}
                    {notifications.map((n) => {
                      const nm = NOTIF_META[n.status] || NOTIF_META.pending;
                      return (
                        <TableRow key={n.id} data-testid="admin-notification-row">
                          <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(n.created_at)}</TableCell>
                          <TableCell className="tabular-nums whitespace-nowrap">{fmtPhone(n.phone)}</TableCell>
                          <TableCell className="text-right tabular-nums font-semibold">{fmtMoney(n.amount)}</TableCell>
                          <TableCell><Badge variant="outline" className={nm.cls}>{nm.label}</Badge></TableCell>
                          <TableCell className="text-center">{(n.attempts || []).length}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Dialog>
                                <DialogTrigger asChild>
                                  <Button data-testid="admin-view-payload-button" size="sm" variant="outline" className="h-8">
                                    <Eye size={14} />
                                  </Button>
                                </DialogTrigger>
                                <DialogContent className="max-w-lg">
                                  <DialogHeader>
                                    <DialogTitle>Payload для API брокера</DialogTitle>
                                  </DialogHeader>
                                  <pre className="bg-muted rounded-lg p-3 text-xs overflow-auto max-h-72">
                                    {JSON.stringify(n.payload, null, 2)}
                                  </pre>
                                  <div className="text-xs text-muted-foreground">
                                    Попытки: {(n.attempts || []).map((a, i) => (
                                      <div key={i} className="mt-1">
                                        {fmtDate(a.at)} — {a.success ? "успех" : "ошибка"}{a.mocked ? " (MOCK)" : ""}{a.http_status ? ` HTTP ${a.http_status}` : ""}
                                      </div>
                                    ))}
                                  </div>
                                </DialogContent>
                              </Dialog>
                              <Button
                                data-testid="admin-retry-notification-button"
                                size="sm"
                                variant="outline"
                                className="h-8"
                                onClick={() => retryNotif(n.id)}
                              >
                                <Send size={14} className="mr-1" /> Повторить
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
          {/* ===== telegram bot tab ===== */}
          <TabsContent value="telegram">
            <TelegramTab />
          </TabsContent>
        </Tabs>

        <p className="text-xs text-muted-foreground">
          {mockMode
            ? "MOCK MODE: эквайринг СБП и API брокера работают в демо-режиме."
            : "Эквайринг: OxyPay (боевой). Уведомления брокера NORD пока в демо-режиме (нет документации API)."}
        </p>
      </main>

      {/* provider health dialog */}
      <Dialog open={healthOpen} onOpenChange={setHealthOpen}>
        <DialogContent className="max-w-lg" data-testid="provider-health-dialog">
          <DialogHeader>
            <DialogTitle>Диагностика шлюза СБП</DialogTitle>
          </DialogHeader>
          {healthLoading && (
            <div className="flex items-center gap-3 py-6 justify-center text-muted-foreground">
              <Activity size={20} className="animate-pulse" /> Проверяем чекаут OxyPay…
            </div>
          )}
          {!healthLoading && health && (
            <div className="flex flex-col gap-3">
              {health.healthy ? (
                <div className="flex items-start gap-3 rounded-xl border border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/10 p-4">
                  <CheckCircle2 className="text-[hsl(var(--success))] shrink-0" size={26} />
                  <div>
                    <div className="font-semibold">Шлюз работает</div>
                    <div className="text-sm text-muted-foreground">Чекаут выдаёт реквизиты СБП — оплата возможна.</div>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3 rounded-xl border border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 p-4">
                  <AlertTriangle className="text-[hsl(var(--destructive))] shrink-0" size={26} />
                  <div>
                    <div className="font-semibold">Сбой на стороне OxyPay</div>
                    <div className="text-sm text-muted-foreground">
                      Чекаут не выдаёт реквизиты СБП: клиент видит вечную страницу «платёж обрабатывается».
                      Создание платежей работает, но провайдер (mtc) не отдаёт QR/ссылку НСПК.
                    </div>
                  </div>
                </div>
              )}
              {!health.healthy && (
                <div className="rounded-xl bg-muted p-4 text-sm">
                  <div className="font-semibold mb-1">Что делать:</div>
                  Обратитесь в поддержку OxyPay: «Провайдер mtc на нашем мерчанте (профиль 24651, ОсОО Агент Минка)
                  не выдаёт реквизиты СБП — /providers/mtc отвечает HTTP 500, платежи зависают в processing.
                  Просим активировать/починить выдачу СБП-реквизитов».
                </div>
              )}
              <pre className="bg-muted rounded-lg p-3 text-xs overflow-auto max-h-40">
                {JSON.stringify(health, null, 2)}
              </pre>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* bulk upload result dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-lg" data-testid="upload-result-dialog">
          <DialogHeader>
            <DialogTitle>Результат загрузки Excel</DialogTitle>
          </DialogHeader>
          {uploadResult && (
            <div className="flex flex-col gap-3">
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-xl bg-[hsl(var(--success))]/10 border border-[hsl(var(--success))]/30 p-3 text-center">
                  <div className="font-display text-3xl font-bold text-[hsl(var(--success))]" data-testid="upload-added-count">{uploadResult.added}</div>
                  <div className="text-xs text-muted-foreground">добавлено</div>
                </div>
                <div className="rounded-xl bg-muted p-3 text-center">
                  <div className="font-display text-3xl font-bold">{uploadResult.skipped_existing}</div>
                  <div className="text-xs text-muted-foreground">уже были</div>
                </div>
                <div className="rounded-xl bg-[hsl(var(--destructive))]/10 border border-[hsl(var(--destructive))]/30 p-3 text-center">
                  <div className="font-display text-3xl font-bold text-[hsl(var(--destructive))]">{uploadResult.invalid_count}</div>
                  <div className="text-xs text-muted-foreground">с ошибкой</div>
                </div>
              </div>
              <div className="text-sm text-muted-foreground">
                Всего номеров в базе: <span className="font-semibold text-foreground">{uploadResult.total_in_base}</span>
              </div>
              {uploadResult.invalid?.length > 0 && (
                <div className="rounded-xl border border-border max-h-52 overflow-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-1.5">Строка</th>
                        <th className="text-left px-3 py-1.5">Значение</th>
                        <th className="text-left px-3 py-1.5">Причина</th>
                      </tr>
                    </thead>
                    <tbody>
                      {uploadResult.invalid.map((r, i) => (
                        <tr key={i} className="border-t border-border">
                          <td className="px-3 py-1.5 tabular-nums">{r.row}</td>
                          <td className="px-3 py-1.5 tabular-nums">{r.raw}</td>
                          <td className="px-3 py-1.5 text-[hsl(var(--destructive))]">{r.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
